import { useRef, useEffect, useState } from "react";
import "./FindRow.scss";

export const CATEGORIES = ["Uncategorized", "Favorites", "Focus", "Relax", "Discover"];

// A compact, Spotify-like saved-track row. Collapsed by default: title, artist,
// category, actions. Expands to reveal the Spotify player + the AI note.
export default function FindRow({
  song,
  index,
  iframeApi,
  onDelete,
  onCategoryChange,
}) {
  const embedRef = useRef();
  const [expanded, setExpanded] = useState(false);
  const [mountedEmbed, setMountedEmbed] = useState(false);

  // Create the Spotify controller only once, the first time the row expands.
  useEffect(() => {
    if (expanded && iframeApi && embedRef.current && !mountedEmbed) {
      iframeApi.createController(
        embedRef.current,
        { width: "100%", height: "152", uri: song.artist_uri },
        () => {}
      );
      setMountedEmbed(true);
    }
  }, [expanded, iframeApi, mountedEmbed, song.artist_uri]);

  return (
    <div className={`find${expanded ? " find--open" : ""}`}>
      <div className="find__row">
        <button
          type="button"
          className="find__lead"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="find__index">
            <span className="find__num">{String(index + 1).padStart(2, "0")}</span>
            <svg className="find__play" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          </span>
          <span className="find__info">
            <span className="find__track">{song.track_title}</span>
            <span className="find__artist">{song.artist_name}</span>
          </span>
        </button>

        <div className="find__controls">
          <label className="sr-only" htmlFor={`cat-${song.id}`}>Category</label>
          <select
            id={`cat-${song.id}`}
            className="find__cat"
            value={CATEGORIES.includes(song.category) ? song.category : "Uncategorized"}
            onChange={(e) => onCategoryChange(song, e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <button
            type="button"
            className="find__expand"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" className={expanded ? "is-open" : ""}>
              <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            className="find__trash"
            onClick={() => onDelete(song)}
            aria-label="Remove from your finds"
          >
            <svg viewBox="0 0 24 24" width="17" height="17">
              <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7"
                fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="find__detail">
          <div className="find__embed" ref={embedRef} />
          {song.chatGPT && (
            <div className="find__why">
              <span className="find__why-label">Why it fits</span>
              <p>{song.chatGPT}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
